// Utility functions for ski spreadsheet upload

const BRANDS = ['Fischer', 'Salomon', 'Atomic', 'Rossignol', 'Madshus'];
const TYPES = ['classic', 'skate'];
const ROSSI_MOLDS = ['S1', 'S2', 'S3', 'SX', 'C1', 'C2', 'C3', 'Zero', 'skin', 'Double Pole'];
const ROSSI_STIFFNESS = ['Soft', 'Medium', 'Stiff'];

// Generate CSV template
export function generateSkiTemplate() {
  const headers = [
    'ski_number',
    'serial_number',
    'brand',
    'type',
    'fa',
    'hr',
    'mf',
    'h4',
    'rossi_flex',
    'rossi_height',
    'rossi_stiffness',
    'mold',
    'grind',
    'base',
    'ski_length_cm',
    'year_made',
    'temperature_color',
    'ski_category'
  ];

  // Simple template with just headers and examples
  const csvContent = [
    headers.join(','),
    '123,102093040192123,Fischer,classic,45,2.5,,,,,,,grind1,base1,180,2023,,race',
    '456,102093040192456,Salomon,skate,,,50,3.0,,,,,grind2,base2,185,2022,,training',
    '789,102093040192789,Rossignol,classic,,,,,45,2.5,Medium,C1,,base3,180,2024,,retired_race'
  ].join('\n');

  return csvContent;
}

// Download CSV template
export function downloadSkiTemplate() {
  const csv = generateSkiTemplate();
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'ski_upload_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Parse CSV file
export function parseCSV(csvText) {
  const lines = csvText.split('\n').filter(line => line.trim());
  
  // Find the header row (first line with "ski_number")
  let headerIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    // Look for a line that contains "ski_number" and looks like CSV headers
    if (line.includes('ski_number') && 
        line.includes(',') && 
        !line.startsWith('#') && 
        line.split(',').length > 5) { // Should have multiple columns
      headerIndex = i;
      break;
    }
  }
  
  if (headerIndex === -1) {
    throw new Error('Could not find header row. Please include "ski_number" in the first row with commas separating columns.');
  }

  const actualHeaderIndex = headerIndex;
  const headers = lines[actualHeaderIndex].split(',').map(h => h.trim());
  
  const data = [];
  for (let i = actualHeaderIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    // Skip empty lines and comment lines
    if (!line || line.startsWith('#')) {
      continue;
    }
    
    const values = parseCSVLine(line);
    if (values.length === 0) continue;
    
    const row = {};
    headers.forEach((header, idx) => {
      const value = values[idx]?.trim() || '';
      row[header] = value === '' ? null : value;
    });
    
    // Only add rows that have at least ski_number
    if (row.ski_number) {
      data.push(row);
    }
  }
  
  return data;
}

// Parse a CSV line handling quoted values
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  
  return result;
}

// Validate ski data
export function validateSkiData(ski, rowIndex) {
  const errors = [];
  const warnings = [];
  
  // Required fields
  if (!ski.ski_number || ski.ski_number.trim() === '') {
    errors.push('Ski number is required');
  }
  
  if (!ski.brand || ski.brand.trim() === '') {
    errors.push('Brand is required');
  } else if (!BRANDS.includes(ski.brand)) {
    errors.push(`Brand must be one of: ${BRANDS.join(', ')}`);
  }
  
  if (!ski.type || ski.type.trim() === '') {
    errors.push('Type is required');
  } else if (!TYPES.includes(ski.type)) {
    errors.push(`Type must be one of: ${TYPES.join(', ')}`);
  }
  
  // Brand-specific validation
  if (ski.brand === 'Fischer') {
    if (ski.fa && isNaN(parseFloat(ski.fa))) {
      errors.push('FA must be a number');
    }
    if (ski.hr && isNaN(parseFloat(ski.hr))) {
      errors.push('HR must be a number');
    }
  }
  
  if (['Salomon', 'Atomic'].includes(ski.brand)) {
    if (ski.mf && isNaN(parseFloat(ski.mf))) {
      errors.push('MF must be a number');
    }
    if (ski.h4 && isNaN(parseFloat(ski.h4))) {
      errors.push('H4 must be a number');
    }
  }
  
  if (ski.brand === 'Rossignol') {
    if (ski.rossi_flex && isNaN(parseFloat(ski.rossi_flex))) {
      errors.push('Rossi Flex must be a number');
    }
    if (ski.rossi_height && isNaN(parseFloat(ski.rossi_height))) {
      errors.push('Rossi Height must be a number');
    }
    if (ski.rossi_stiffness && !ROSSI_STIFFNESS.includes(ski.rossi_stiffness)) {
      errors.push(`Rossi Stiffness must be one of: ${ROSSI_STIFFNESS.join(', ')}`);
    }
    if (ski.mold && !ROSSI_MOLDS.includes(ski.mold)) {
      errors.push(`Mold (Rossignol) must be one of: ${ROSSI_MOLDS.join(', ')}`);
    }
  }
  
  // Year validation
  if (ski.year_made && (isNaN(parseInt(ski.year_made)) || parseInt(ski.year_made) < 1900 || parseInt(ski.year_made) > new Date().getFullYear() + 1)) {
    warnings.push('Year made should be a valid year');
  }
  
  // Temperature color validation (optional, 0-100)
  if (ski.temperature_color) {
    const tempVal = parseFloat(ski.temperature_color);
    if (isNaN(tempVal)) {
      errors.push('Temperature color must be a number');
    } else if (tempVal < 0 || tempVal > 100) {
      errors.push('Temperature color must be between 0 and 100');
    }
  }
  
  // Ski category validation (optional)
  if (ski.ski_category && ski.ski_category.trim() !== '') {
    const validCategories = ['race', 'retired_race', 'training'];
    if (!validCategories.includes(ski.ski_category.trim())) {
      errors.push(`Ski category must be one of: ${validCategories.join(', ')}`);
    }
  }
  
  return { errors, warnings, isValid: errors.length === 0 };
}

// Validate all skis
export function validateAllSkis(skis) {
  return skis.map((ski, idx) => ({
    ...ski,
    rowIndex: idx + 1,
    validation: validateSkiData(ski, idx + 1)
  }));
}

